/**
 * Subscription checkout helpers — money display + invoice session restore.
 * CommonJS so contract tests can require() without Babel.
 */

var INVOICE_CHECKOUT_SESSION_KEY = "pentecost_invoice_checkout";
var PAYMENT_SUCCESS_SESSION_KEY = "pentecost_payment_success";

/**
 * Format a numeric amount to exactly two decimal places for UI display.
 * Avoids float artifacts like 0.010000000000000009.
 */
function formatMoneyAmount(amount) {
  var n = Number(amount);
  if (!isFinite(n)) {
    return "0.00";
  }
  return n.toFixed(2);
}

/**
 * Currency symbol/code + two-decimal amount (e.g. "$0.01").
 */
function formatMoneyDisplay(currency, amount) {
  return String(currency == null ? "" : currency) + formatMoneyAmount(amount);
}

/**
 * Numeric total suitable for PayPal Express (two-decimal precision).
 */
function moneyNumber(amount) {
  return Number(formatMoneyAmount(amount));
}

/**
 * Resolve which saved card should be active (explicit selection, else default).
 */
function resolveSelectedCardId(cards, preferredId) {
  if (!cards || !cards.length) {
    return null;
  }
  if (preferredId != null) {
    for (var i = 0; i < cards.length; i++) {
      if (String(cards[i].card_id) === String(preferredId)) {
        return cards[i].card_id;
      }
    }
  }
  for (var j = 0; j < cards.length; j++) {
    if (cards[j].is_default) {
      return cards[j].card_id;
    }
  }
  return cards[0].card_id;
}

function saveInvoiceCheckoutSession(payload) {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  try {
    sessionStorage.setItem(
      INVOICE_CHECKOUT_SESSION_KEY,
      JSON.stringify(payload || {})
    );
  } catch (e) {
    // ignore quota / private mode
  }
}

function loadInvoiceCheckoutSession() {
  if (typeof sessionStorage === "undefined") {
    return null;
  }
  try {
    var raw = sessionStorage.getItem(INVOICE_CHECKOUT_SESSION_KEY);
    if (!raw) {
      return null;
    }
    var parsed = JSON.parse(raw);
    if (!parsed || !parsed.subscription) {
      return null;
    }
    return parsed;
  } catch (e) {
    return null;
  }
}

function clearInvoiceCheckoutSession() {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  try {
    sessionStorage.removeItem(INVOICE_CHECKOUT_SESSION_KEY);
  } catch (e) {
    // ignore
  }
}

/**
 * Build a trusted payment-success confirmation from API data + checkout snapshot.
 * Does not invent payment_id; only forwards what the API returned.
 */
function buildPaymentSuccessConfirmation(apiResponseData, checkoutSnapshot) {
  var data =
    apiResponseData && apiResponseData.data && typeof apiResponseData.data === "object"
      ? apiResponseData.data
      : {};
  var snap = checkoutSnapshot && typeof checkoutSnapshot === "object" ? checkoutSnapshot : {};
  var paymentId = data.payment_id;
  return {
    payment_id: paymentId == null || paymentId === "" ? "" : String(paymentId),
    planTitle: snap.title == null ? "" : String(snap.title),
    planTerm: snap.plan_formatted == null ? "" : String(snap.plan_formatted),
    currency: snap.currency == null ? "" : snap.currency,
    amount: snap.amount != null ? snap.amount : 0,
  };
}

function isPaymentSuccessConfirmation(payload) {
  return !!(
    payload &&
    typeof payload === "object" &&
    (payload.payment_id || payload.planTitle)
  );
}

function savePaymentSuccessConfirmation(payload) {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  if (!isPaymentSuccessConfirmation(payload)) {
    return;
  }
  try {
    sessionStorage.setItem(
      PAYMENT_SUCCESS_SESSION_KEY,
      JSON.stringify(payload)
    );
  } catch (e) {
    // ignore quota / private mode
  }
}

function loadPaymentSuccessConfirmation() {
  if (typeof sessionStorage === "undefined") {
    return null;
  }
  try {
    var raw = sessionStorage.getItem(PAYMENT_SUCCESS_SESSION_KEY);
    if (!raw) {
      return null;
    }
    var parsed = JSON.parse(raw);
    return isPaymentSuccessConfirmation(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
}

function clearPaymentSuccessConfirmation() {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  try {
    sessionStorage.removeItem(PAYMENT_SUCCESS_SESSION_KEY);
  } catch (e) {
    // ignore
  }
}

/**
 * Prefer router location.state (same navigation), else sessionStorage (refresh).
 * Never reads URL query/search params as proof of payment.
 */
function resolvePaymentSuccessConfirmation(locationState) {
  if (
    locationState &&
    isPaymentSuccessConfirmation(locationState.paymentSuccess)
  ) {
    return locationState.paymentSuccess;
  }
  return loadPaymentSuccessConfirmation();
}

/**
 * Exclusive payment-mode radio checked map (for tests / shared logic).
 */
function paymentModeCheckedMap(paymentMode, modes) {
  var list = modes || ["paypal", "card", "razorpay", "paystack"];
  var out = {};
  for (var i = 0; i < list.length; i++) {
    out[list[i]] = list[i] === paymentMode;
  }
  return out;
}

/**
 * Admin Payment Settings → "PayPal at Checkout" (Settings key is_paypal_enabled).
 * Visible only for an explicit 1 / "1" / true; missing or anything else = hidden.
 * Stripe (card) is never gated by this.
 */
function isPaypalCheckoutVisible(configData) {
  if (!configData || typeof configData !== "object") {
    return false;
  }
  var flag = configData.is_paypal_enabled;
  return flag === true || flag === 1 || flag === "1";
}

/**
 * Payment mode to use when PayPal is hidden: "paypal" falls back to "card".
 */
function resolveCheckoutPaymentMode(paymentMode, paypalVisible) {
  if (!paymentMode) {
    return "card";
  }
  if (paymentMode === "paypal" && !paypalVisible) {
    return "card";
  }
  return paymentMode;
}

/**
 * PayPal Express (checkout.js) button style — iframe sized by SDK only.
 * Matches Bootstrap .btn height (~38px); width comes from wrapper CSS.
 * Default without this is size "small" (150×25), which looks smaller than Card.
 */
var PAYPAL_CHECKOUT_BUTTON_STYLE = {
  size: "responsive",
  height: 38,
  shape: "rect",
  label: "paypal",
  tagline: false,
};

module.exports = {
  INVOICE_CHECKOUT_SESSION_KEY: INVOICE_CHECKOUT_SESSION_KEY,
  PAYMENT_SUCCESS_SESSION_KEY: PAYMENT_SUCCESS_SESSION_KEY,
  formatMoneyAmount: formatMoneyAmount,
  formatMoneyDisplay: formatMoneyDisplay,
  moneyNumber: moneyNumber,
  resolveSelectedCardId: resolveSelectedCardId,
  saveInvoiceCheckoutSession: saveInvoiceCheckoutSession,
  loadInvoiceCheckoutSession: loadInvoiceCheckoutSession,
  clearInvoiceCheckoutSession: clearInvoiceCheckoutSession,
  buildPaymentSuccessConfirmation: buildPaymentSuccessConfirmation,
  isPaymentSuccessConfirmation: isPaymentSuccessConfirmation,
  savePaymentSuccessConfirmation: savePaymentSuccessConfirmation,
  loadPaymentSuccessConfirmation: loadPaymentSuccessConfirmation,
  clearPaymentSuccessConfirmation: clearPaymentSuccessConfirmation,
  resolvePaymentSuccessConfirmation: resolvePaymentSuccessConfirmation,
  paymentModeCheckedMap: paymentModeCheckedMap,
  isPaypalCheckoutVisible: isPaypalCheckoutVisible,
  resolveCheckoutPaymentMode: resolveCheckoutPaymentMode,
  PAYPAL_CHECKOUT_BUTTON_STYLE: PAYPAL_CHECKOUT_BUTTON_STYLE,
};
